import { type TokenName } from './tokens-types';

const miscellaneousColorMapTokenNames: Record<string, TokenName> = {
  borderDefaultColor: 'color.border',
  borderDisabled: 'color.border.disabled',
  borderFocused: 'color.border.focused',
  sectionTextGrey: 'color.text.accent.gray',
  sectionTextWhite: 'color.text.inverse',
  chartGrayBoldestHovered: 'color.chart.gray.boldest.hovered',
  textDisabled: 'color.text.disabled',
  textSubtle: 'color.text.subtle',
  textSubtlest: 'color.text.subtlest',
  backgroundDisabled: 'color.background.disabled',
  backgroundInput: 'color.background.input',
  backgroundInputHovered: 'color.background.input.hovered',
  backgroundAccentGray: 'color.background.accent.gray.subtler',
  iconDefaultColor: 'color.icon',
  iconSubtle: 'color.icon.subtle',
  iconDisabled: 'color.icon.disabled',
  backgroundAccentBlueSubtlest: 'color.background.accent.blue.subtlest',
  backgroundAccentBlueSubtlestHovered: 'color.background.accent.blue.subtlest.hovered',
  currentUserSelectionColor: 'color.border.focused',
  multiSelectionColor: 'color.background.selected.pressed',
  privateSelectionColor: 'color.background.accent.gray.subtler.pressed',
  lockedSelectionColor: 'color.background.accent.gray.subtlest.pressed',
};

export const textColorMapTokenNames: Record<string, TokenName> = {
  textDefaultColor: 'color.text',
  textAccentBlue: 'color.text.accent.blue',
  textAccentPurple: 'color.text.accent.purple',
  textAccentMagenta: 'color.text.accent.magenta',
  textAccentRed: 'color.text.accent.red',
  textAccentOrange: 'color.text.accent.orange',
  textAccentYellow: 'color.text.accent.yellow',
  textAccentLime: 'color.text.accent.lime',
  textAccentGreen: 'color.text.accent.green',
  textAccentTeal: 'color.text.accent.teal',
};

export const iconColorMapTokenNames: Record<string, TokenName> = {
  iconAccentGray: 'color.icon.accent.gray',
  iconAccentBlue: 'color.icon.accent.blue',
  iconAccentPurple: 'color.icon.accent.purple',
  iconAccentMagenta: 'color.icon.accent.magenta',
  iconAccentRed: 'color.icon.accent.red',
  iconAccentOrange: 'color.icon.accent.orange',
  iconAccentYellow: 'color.border.accent.yellow',
  iconAccentLime: 'color.icon.accent.lime',
  iconAccentGreen: 'color.icon.accent.green',
  iconAccentTeal: 'color.icon.accent.teal',
};

export const backgroundColorMapTokenNames: Record<string, TokenName> = {
  neutralLight: 'elevation.surface',
  backgroundAccentBlue: 'color.background.accent.blue.subtler',
  backgroundAccentPurple: 'color.background.accent.purple.subtler',
  backgroundAccentMagenta: 'color.background.accent.magenta.subtler',
  backgroundAccentRed: 'color.background.accent.red.subtler',
  backgroundAccentOrange: 'color.background.accent.orange.subtler',
  backgroundAccentYellow: 'color.background.accent.yellow.subtler',
  backgroundAccentLime: 'color.background.accent.lime.subtlest.hovered',
  backgroundAccentGreen: 'color.background.accent.green.subtlest.hovered',
  backgroundAccentTeal: 'color.background.accent.teal.subtlest.hovered',
};

const shadowColorMapTokenNames: Record<string, TokenName> = {
  shadowOverflowSpread: 'elevation.shadow.overflow.spread',
  shadowOverflowPerimeter: 'elevation.shadow.overflow.perimeter',
};

export const strongestColorMapTokenNames: Record<string, TokenName> = {
  n600: 'color.border.accent.gray',
  b600: 'color.border.accent.blue',
  p600: 'color.border.accent.purple',
  m600: 'color.border.accent.magenta',
  r600: 'color.border.accent.red',
  o600: 'color.border.accent.orange',
  y600: 'color.border.accent.yellow',
  l600: 'color.border.accent.lime',
  g600: 'color.border.accent.green',
  t600: 'color.border.accent.teal',
};

export const strongColorMapTokenNames: Record<string, TokenName> = {
  n400: 'color.background.accent.gray.subtler.hovered',
  b300: 'color.background.accent.blue.subtlest.pressed',
  p300: 'color.background.accent.purple.subtlest.pressed',
  m300: 'color.background.accent.magenta.subtlest.pressed',
  r300: 'color.background.accent.red.subtlest.pressed',
  o300: 'color.background.accent.orange.subtlest.pressed',
  y300: 'color.background.accent.yellow.subtlest.pressed',
  l300: 'color.background.accent.lime.subtlest.pressed',
  g300: 'color.background.accent.green.subtlest.pressed',
  t300: 'color.background.accent.teal.subtlest.pressed',
};

export const mediumColorMapTokenNames: Record<string, TokenName> = {
  n200: 'color.background.accent.gray.subtlest',
  b200: 'color.background.accent.blue.subtler',
  p200: 'color.background.accent.purple.subtler',
  m200: 'color.background.accent.magenta.subtler',
  red: 'color.background.accent.red.subtler',
  orange: 'color.background.accent.orange.subtler',
  y150: 'color.background.accent.yellow.subtler',
  l200: 'color.background.accent.lime.subtlest.hovered',
  g150: 'color.background.accent.green.subtlest.hovered',
  teal: 'color.background.accent.teal.subtlest.hovered',
};

export const lightColorMapTokenNames: Record<string, TokenName> = {
  white: 'color.background.input',
  b100: 'color.background.accent.blue.subtlest',
  p100: 'color.background.accent.purple.subtlest',
  m100: 'color.background.accent.magenta.subtlest',
  r100: 'color.background.accent.red.subtlest',
  o100: 'color.background.accent.orange.subtlest',
  y100: 'color.background.accent.yellow.subtlest',
  l100: 'color.background.accent.lime.subtlest',
  g100: 'color.background.accent.green.subtlest',
  t100: 'color.background.accent.teal.subtlest',
};

const outlineColorMapTokenNames: Record<string, TokenName> = {
  n800: 'color.text.accent.gray',
  b800: 'color.text.accent.blue',
  p800: 'color.text.accent.purple',
  m800: 'color.text.accent.magenta',
  r800: 'color.text.accent.red',
  o800: 'color.text.accent.orange',
  y800: 'color.text.accent.yellow',
  l800: 'color.text.accent.lime',
  g800: 'color.text.accent.green',
  t800: 'color.text.accent.teal',
};

export const allColorMapTokenNames: Record<string, TokenName> = {
  none: 'color.text.accent.gray.bolder',
  y250: 'color.background.accent.yellow.subtler.hovered', // for backwards compatibility
  n300: 'color.background.accent.gray.subtlest.hovered',
  ...lightColorMapTokenNames,
  ...mediumColorMapTokenNames,
  ...strongColorMapTokenNames,
  ...strongestColorMapTokenNames,
  ...backgroundColorMapTokenNames,
  ...iconColorMapTokenNames,
  ...textColorMapTokenNames,
  ...outlineColorMapTokenNames,
  ...miscellaneousColorMapTokenNames,
  ...shadowColorMapTokenNames,
};
